/**
 * Renders configurable asset facts in the review table while keeping selection
 * and detail navigation available regardless of saved view preferences.
 */
import React, { useMemo, useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import { type OnSort, type SortDirection } from '@amzn/meridian/data-table/types/column-sort';
import Icon from '@amzn/meridian/icon';
import Image from '@amzn/meridian/image';
import Link from '@amzn/meridian/link';
import Popover from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import errorTokens from '@amzn/meridian-tokens/base/icon/error';
import globeTokens from '@amzn/meridian-tokens/base/icon/globe';
import { useTranslation } from 'src/arb-wrapper';
import { PublisherDisplay } from 'src/components/common/PublisherDisplay';
import { BenderAssetType } from 'src/constants';
import {
  type AssetDto,
  type CampaignDesiredStateDto,
} from 'src/store/generated/leela-api-generated';
import { sanitizeMeridianImageSrc } from 'src/utils';
import { formatDimensions, formatVideoDuration } from 'src/utils/assetFieldFormatters';
import { assetStrategyKey, isSameAssetStrategy } from 'src/utils/assetKeys';
import { extractFilenameFromAsset } from 'src/utils/AssetUtils';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { resolveStrategyLabel } from 'src/utils/strategyLabels';

import { shouldShowAdspStvWarning } from './AssetCard';
import { AssetSourceTag } from './AssetSourceTag';
import {
  ASSET_VIEW_FIELDS,
  getFieldWidth,
  isFieldVisible,
  type AssetViewFieldId,
  type AssetViewPreferences,
} from './assetViewFields';
import { RELEASE_DATE_FALLBACK, ReleaseDateSkeleton } from './release-date-display';

const UNAVAILABLE_VALUE = '—';

interface TruncatedCellProps {
  readonly children?: React.ReactNode;
  readonly value: string;
}

function TruncatedCell ({ children, value }: TruncatedCellProps): React.JSX.Element {
  return (
    <span
      style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      title={value}
    >
      {children ?? value}
    </span>
  );
}

function getFieldLabelKey (id: AssetViewFieldId): string {
  return ASSET_VIEW_FIELDS.find((field) => field.id === id)?.labelKey ?? '';
}

function getReleaseDateDisplayValue (
  titles: AssetDto['titles'],
  releaseDatesByGti: ReadonlyMap<string, string | undefined> | undefined,
): string {
  if (titles.length === 0) return RELEASE_DATE_FALLBACK;
  return titles
    .map(({ gti }) => formatUtcDate(releaseDatesByGti?.get(gti), RELEASE_DATE_FALLBACK))
    .join(', ');
}

function getThumbnailFromOptions (asset: AssetDto): string {
  if (!asset.thumbnailOptions?.length) return '';

  const approvedThumbnail = asset.thumbnailOptions.find(
    (option) => option.approvalStatus === 'APPROVED',
  );

  return approvedThumbnail?.assetUrl ?? asset.thumbnailOptions[0].assetUrl;
}

function getDestinationUrls (asset: AssetDto): AssetDto['destinationUrls'] {
  const urls = asset.destinationUrls.length > 0
    ? asset.destinationUrls
    : asset.destinationUrl
      ? [{ campaignId: 'default', url: asset.destinationUrl }]
      : [];
  const seen = new Set<string>();

  return urls.filter(({ campaignId, url }) => {
    const key = `${campaignId}::${url}`;
    if (!url || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formatSchedule (asset: AssetDto): string {
  if (!asset.schedule) return UNAVAILABLE_VALUE;

  return `${formatUtcDate(asset.schedule.startTime, UNAVAILABLE_VALUE)} – ${formatUtcDate(asset.schedule.endTime, UNAVAILABLE_VALUE)}`;
}

function formatChannel (asset: AssetDto): string {
  const channel = asset.metadata?.channel?.join(', ');
  return channel === undefined || channel.length === 0
    ? UNAVAILABLE_VALUE
    : channel;
}

function formatTitle (asset: AssetDto): string {
  const fullText = asset.titles.map((title) => title.name).join(', ');
  return fullText.length > 30 ? `${fullText.substring(0, 30)}...` : fullText;
}

export interface AssetTableProps {
  assets: (object[] & AssetDto[]) | undefined;
  campaigns: (object[] & CampaignDesiredStateDto[]) | undefined;
  selectedAssets: AssetDto[];
  releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  isReleaseDatesFetching?: boolean;
  sortColumn?: string;
  sortDirection?: SortDirection;
  handleAssetSelect: (asset: AssetDto) => void;
  onOpenDetail: (asset: AssetDto) => void;
  preferences: AssetViewPreferences;
  setSelectedAssets: (assets: AssetDto[]) => void;
  onSortChange: (sortColumn: string, sortDirection: 'ascending' | 'descending') => void;
  strategyLabels?: Map<string, string>;
}

interface AssetTableRowProps {
  readonly asset: AssetDto;
  readonly campaigns: (object[] & CampaignDesiredStateDto[]) | undefined;
  readonly isReleaseDatesFetching?: boolean;
  readonly isSelected: boolean;
  readonly preferences: AssetViewPreferences;
  readonly releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  readonly strategyLabels?: Map<string, string>;
  readonly handleAssetSelect: (asset: AssetDto) => void;
  readonly onOpenDetail: (asset: AssetDto) => void;
}

function assetTableRowPropsAreEqual (
  previous: AssetTableRowProps,
  next: AssetTableRowProps,
): boolean {
  return previous.asset === next.asset &&
    previous.campaigns === next.campaigns &&
    previous.handleAssetSelect === next.handleAssetSelect &&
    previous.isReleaseDatesFetching === next.isReleaseDatesFetching &&
    previous.isSelected === next.isSelected &&
    previous.onOpenDetail === next.onOpenDetail &&
    previous.preferences === next.preferences &&
    previous.releaseDatesByGti === next.releaseDatesByGti &&
    previous.strategyLabels === next.strategyLabels;
}

const AssetTableRow = React.memo<AssetTableRowProps>(({
  asset,
  campaigns,
  isReleaseDatesFetching,
  isSelected,
  preferences,
  releaseDatesByGti,
  strategyLabels,
  handleAssetSelect,
  onOpenDetail,
}: AssetTableRowProps) => {
  const { t } = useTranslation();
  const [isDestinationPopoverOpen, setIsDestinationPopoverOpen] = useState(false);
  const destinationButtonRef = useRef();
  const destinationUrls = useMemo(() => getDestinationUrls(asset), [asset]);
  const channelDisplay = formatChannel(asset);
  const filenameDisplay = extractFilenameFromAsset(asset, t);
  const filenameTitle = React.isValidElement<{ children?: React.ReactNode }>(filenameDisplay)
    ? String(filenameDisplay.props.children ?? '')
    : String(filenameDisplay);
  const liveStatusDisplay = asset.metadata?.liveStatus ?? UNAVAILABLE_VALUE;
  const scheduleDisplay = formatSchedule(asset);
  const strategyDisplay = resolveStrategyLabel(strategyLabels, asset.productStrategyId) || UNAVAILABLE_VALUE;

  return (
    <DataTableRow
      data-testid={`asset-table-row-${asset.assetId}`}
      key={assetStrategyKey(asset)}
    >
      <DataTableCell>
        <Checkbox
          checked={isSelected}
          data-testid={`asset-table-checkbox-${asset.assetId}`}
          value={isSelected}
          onChange={() => { handleAssetSelect(asset); }}
        />
      </DataTableCell>
      <DataTableCell>
        <Row>
          <div
            data-testid={`asset-thumbnail-${asset.assetId}`}
            style={{ cursor: 'pointer' }}
            onClick={() => { onOpenDetail(asset); }}
          >
            <div style={{ height: '64px', width: '64px' }}>
              <Image
                size="contain"
                src={sanitizeMeridianImageSrc(
                  asset.assetType === BenderAssetType.VIDEO
                    ? getThumbnailFromOptions(asset)
                    : asset.assetUrl,
                )}
                viewportAspectRatio={1}
              />
            </div>
          </div>
          <Column spacing="200">
            <Button
              data-testid={`asset-detail-button-${asset.assetId}`}
              size="medium"
              type="link"
              onClick={() => { onOpenDetail(asset); }}
            >
              {formatTitle(asset)}
            </Button>
          </Column>
        </Row>
      </DataTableCell>
      {isFieldVisible(preferences, 'releaseDate') && (
        <DataTableCell data-testid={`asset-table-release-date-${asset.assetId}`}>
          {isReleaseDatesFetching
            ? <ReleaseDateSkeleton testId={`asset-table-release-date-skeleton-${asset.assetId}`} />
            : getReleaseDateDisplayValue(asset.titles, releaseDatesByGti)}
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'filename') && (
        <DataTableCell>
          <TruncatedCell value={filenameTitle}>{filenameDisplay}</TruncatedCell>
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'campaigns') && (
        <DataTableCell>
          {campaigns && (
            <TruncatedCell value={asset.campaignIds.join(', ')}>
              <CampaignIdList campaignIds={asset.campaignIds} campaigns={campaigns} />
            </TruncatedCell>
          )}
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'strategies') && (
        <DataTableCell>
          <TruncatedCell value={strategyDisplay}>
            <Text data-testid={`asset-strategy-${asset.assetId}`}>
              {strategyDisplay}
            </Text>
          </TruncatedCell>
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'source') && (
        <DataTableCell>
          <AssetSourceTag generationMethods={asset.metadata?.generationMethods} />
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'locale') && (
        <DataTableCell>{asset.locale}</DataTableCell>
      )}
      {isFieldVisible(preferences, 'assetType') && (
        <DataTableCell>
          {asset.assetType}{' '}
          {shouldShowAdspStvWarning(asset) && (
            <Tooltip id="adsp-video-message" position="end" title={t('adsp_video_stv_warning')}>
              <Icon color="error" data-testid="adsp-stv-warning-icon" tokens={errorTokens} />
            </Tooltip>
          )}
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'liveStatus') && (
        <DataTableCell data-testid={`asset-table-liveStatus-${asset.assetId}`}>
          <TruncatedCell value={liveStatusDisplay} />
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'schedule') && (
        <DataTableCell data-testid={`asset-table-schedule-${asset.assetId}`}>
          <TruncatedCell value={scheduleDisplay} />
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'channel') && (
        <DataTableCell data-testid={`asset-table-channel-${asset.assetId}`}>
          <TruncatedCell value={channelDisplay} />
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'dimensions') && (
        <DataTableCell data-testid={`asset-table-dimensions-${asset.assetId}`}>
          {formatDimensions(asset.metadata?.width, asset.metadata?.height)}
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'duration') && (
        <DataTableCell data-testid={`asset-table-duration-${asset.assetId}`}>
          {formatVideoDuration(
            asset.assetType === BenderAssetType.VIDEO
              ? asset.metadata?.videoDuration
              : undefined,
          )}
        </DataTableCell>
      )}
      {isFieldVisible(preferences, 'destinationUrl') && (
        <DataTableCell data-testid={`asset-table-destinationUrl-${asset.assetId}`}>
          {destinationUrls.length > 0
            ? (
              <>
                <Button
                  aria-label={t('asset_view_field_destinationUrl')}
                  data-testid={`asset-table-destination-urls-btn-${asset.assetId}`}
                  ref={destinationButtonRef}
                  type="icon"
                  onClick={() => { setIsDestinationPopoverOpen(true); }}
                >
                  <Icon tokens={globeTokens} />
                </Button>
                <Popover
                  anchorNode={destinationButtonRef.current}
                  open={isDestinationPopoverOpen}
                  position="bottom"
                  onClose={() => { setIsDestinationPopoverOpen(false); }}
                >
                  <Column>
                    {destinationUrls.map(({ campaignId, url }) => (
                      <Link
                        data-testid={`asset-destination-link-${asset.assetId}-${campaignId}`}
                        href={url}
                        key={`${campaignId}::${url}`}
                        target="_blank"
                        type="secondary"
                      >
                        {url}
                      </Link>
                    ))}
                  </Column>
                </Popover>
              </>
              )
            : UNAVAILABLE_VALUE}
        </DataTableCell>
      )}
    </DataTableRow>
  );
}, assetTableRowPropsAreEqual);

AssetTableRow.displayName = 'AssetTableRow';

export const AssetTable = ({
  assets,
  campaigns,
  selectedAssets,
  releaseDatesByGti,
  isReleaseDatesFetching,
  handleAssetSelect,
  onOpenDetail,
  onSortChange,
  preferences,
  sortColumn,
  sortDirection,
  strategyLabels,
}: AssetTableProps) => {
  const { t } = useTranslation();
  const rows: AssetDto[] = assets ?? [];
  const visibleFieldIds = ASSET_VIEW_FIELDS.filter((field) =>
    isFieldVisible(preferences, field.id),
  );
  const columnCount = visibleFieldIds.length + 1;

  const onSort: OnSort = ({ sortColumn, sortDirection }) => {
    if (sortDirection !== 'none') onSortChange(sortColumn, sortDirection);
  };

  return (
    <div data-testid="asset-table-container" style={{ overflowX: 'auto', width: '100%' }}>
      <DataTable
        data-testid="asset-table"
        layout="fixed"
        showBorder={true}
        showRowDividers={true}
        size="medium"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        stickyHeaderColumn={true}
        stickyHeaderColumnOffset={0}
        stickyHeaderRows={true}
        width="100%"
        onSort={onSort}
      >
        <DataTableHead backgroundColor="secondary">
          <DataTableRow>
            <DataTableHeader width={48}></DataTableHeader>
            <DataTableHeader data-testid="asset-header-titles" sortColumn="titles" stickyColumn={true} stickyColumnOffset={48} width={getFieldWidth('title')}>
              <TruncatedCell value={t(getFieldLabelKey('title'))} />
            </DataTableHeader>
            {isFieldVisible(preferences, 'releaseDate') && (
              <DataTableHeader data-testid="asset-header-releaseDate" width={getFieldWidth('releaseDate')}>
                <TruncatedCell value={t(getFieldLabelKey('releaseDate'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'filename') && (
              <DataTableHeader data-testid="asset-header-filename" width={getFieldWidth('filename')}>
                <TruncatedCell value={t(getFieldLabelKey('filename'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'campaigns') && (
              <DataTableHeader data-testid="asset-header-campaigns" width={getFieldWidth('campaigns')}>
                <TruncatedCell value={t(getFieldLabelKey('campaigns'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'strategies') && (
              <DataTableHeader data-testid="asset-header-strategies" width={getFieldWidth('strategies')}>
                <TruncatedCell value={t(getFieldLabelKey('strategies'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'source') && (
              <DataTableHeader data-testid="asset-header-source" width={getFieldWidth('source')}>
                <TruncatedCell value={t(getFieldLabelKey('source'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'locale') && (
              <DataTableHeader data-testid="asset-header-locales" sortColumn="locale" width={getFieldWidth('locale')}>
                <TruncatedCell value={t(getFieldLabelKey('locale'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'assetType') && (
              <DataTableHeader data-testid="asset-header-assetType" sortColumn="assetType" width={getFieldWidth('assetType')}>
                <TruncatedCell value={t(getFieldLabelKey('assetType'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'liveStatus') && (
              <DataTableHeader data-testid="asset-header-liveStatus" width={getFieldWidth('liveStatus')}>
                <TruncatedCell value={t(getFieldLabelKey('liveStatus'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'schedule') && (
              <DataTableHeader data-testid="asset-header-schedule" width={getFieldWidth('schedule')}>
                <TruncatedCell value={t(getFieldLabelKey('schedule'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'channel') && (
              <DataTableHeader data-testid="asset-header-channel" width={getFieldWidth('channel')}>
                <TruncatedCell value={t(getFieldLabelKey('channel'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'dimensions') && (
              <DataTableHeader data-testid="asset-header-dimensions" width={getFieldWidth('dimensions')}>
                <TruncatedCell value={t(getFieldLabelKey('dimensions'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'duration') && (
              <DataTableHeader data-testid="asset-header-duration" width={getFieldWidth('duration')}>
                <TruncatedCell value={t(getFieldLabelKey('duration'))} />
              </DataTableHeader>
            )}
            {isFieldVisible(preferences, 'destinationUrl') && (
              <DataTableHeader data-testid="asset-header-destinationUrl" width={getFieldWidth('destinationUrl')}>
                <TruncatedCell value={t(getFieldLabelKey('destinationUrl'))} />
              </DataTableHeader>
            )}
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {rows.length === 0 && (
            <DataTableRow>
              <DataTableCell colSpan={columnCount}>{t('table_no_results')}</DataTableCell>
            </DataTableRow>
          )}
          {rows.map((asset) => (
            <AssetTableRow
              asset={asset}
              campaigns={campaigns}
              handleAssetSelect={handleAssetSelect}
              isReleaseDatesFetching={isReleaseDatesFetching}
              isSelected={selectedAssets.some((selected) => isSameAssetStrategy(selected, asset))}
              key={assetStrategyKey(asset)}
              preferences={preferences}
              releaseDatesByGti={releaseDatesByGti}
              strategyLabels={strategyLabels}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </DataTableBody>
      </DataTable>
    </div>
  );
};

const CAMPAIGN_DISPLAY_LIMIT = 5;

export const CampaignIdList = (props: {
  campaignIds: string[];
  campaigns: object[] & CampaignDesiredStateDto[];
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasMore = props.campaignIds.length > CAMPAIGN_DISPLAY_LIMIT;
  const visibleIds = expanded ? props.campaignIds : props.campaignIds.slice(0, CAMPAIGN_DISPLAY_LIMIT);

  return (
    <Column>
      {visibleIds.map((campaignId: string) => {
        const campaign = props.campaigns.find(
          (campaign: CampaignDesiredStateDto) => {
            return String(campaign.campaignId) === String(campaignId);
          },
        );

        return (
          <Row key={campaignId} spacing="200">
            <PublisherDisplay publisher={campaign?.publisher ?? ''} spacing="100" textType="b200" />
            <Text type="b300">{campaignId}</Text>
          </Row>
        );
      })}
      {hasMore && (
        <Button
          data-testid="campaign-list-toggle"
          size="small"
          type="link"
          onClick={() => { setExpanded((previous) => !previous); }}
        >
          {expanded
            ? t('show_less')
            : t('show_more_campaigns', { count: props.campaignIds.length - CAMPAIGN_DISPLAY_LIMIT })}
        </Button>
      )}
    </Column>
  );
};
